'use client'
import clsx from 'clsx'
import { Image, Product } from 'cms-types'
import { LazyMotion, m, useScroll, useTransform } from 'framer-motion'
import { useLayoutEffect, useRef, useState } from 'react'
import { printMoney } from 'shopify-types/helpers'

import Button from '../../components/Button'
import ProductCard from '../../components/ProductCard'
import { useWindowSize } from '../../hooks/useWindowSize'
import { Icon, LoadableComponentProps, LoadingText, PayloadImage } from '../../index'

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

export type SetCreatorProduct = Omit<Product, 'id'> & {
  pageUrl: string
  description?: string
  imageAlt: string
  currency: string
  isAvailable: boolean
  outOfStockTag: {
    icon?: string
    title: string
  }
  productId: string
  variantId: string
  price: string
  title: string
  image: Image
  id?: number
}

type SetCreatorProps = LoadableComponentProps<{
  selectedVariants: Array<SetCreatorProduct>
  variants: SetCreatorProduct[]
  onSelectVariant: (variant: SetCreatorProduct) => void
  onDeselectVariant: (itemId?: number) => void
  onBuyButtonClick: () => void
  isSubmitting: boolean
  discount: number
  discountedPrice: string
  totalPrice: string
  personalSetText: string
  buyButtonText: string
  addProductText: string
  discountText: string
  emptySelectedListText: string
  infoText?: string | null
  accessibility: {
    deselectItemButton: string
    selectItemButton: string
    previousPrice: string
    currentPrice: string
    addedItem: string
    setItem: string
    price: string
    compareAtPrice: string
  }
}>

export default function SetCreator({ data, loading }: SetCreatorProps) {
  const [productDropdownOpen, setProductDropdownOpen] = useState(false)
  const { windowSize } = useWindowSize()
  const leftSide = useRef<HTMLDivElement>(null)
  const rightSide = useRef<HTMLDivElement>(null)

  const { scrollY } = useScroll()
  const leftSectionHeight = leftSide.current?.offsetHeight || 0
  const [rightSectionHeight, setRightSectionHeight] = useState(0)

  useLayoutEffect(() => {
    const { height } = rightSide.current?.getBoundingClientRect() || { height: 0 }
    setRightSectionHeight(height)
  }, [data?.selectedVariants.length])

  const startScroll = leftSide.current?.offsetTop ? leftSide.current.offsetTop - 120 : 0
  const endScroll = leftSectionHeight - rightSectionHeight + startScroll
  const end = leftSectionHeight - rightSectionHeight

  const y = useTransform(scrollY, [startScroll, endScroll], [0, end])

  const productPlaceholder = (
    <div className='radius-xs h-[65px] w-full border border-dashed border-darkgray p-4 lg:h-[82px] lg:rounded-lg lg:p-6'>
      <p className='text-darkgray'>
        {loading ? <LoadingText textLength={20} inline /> : data.emptySelectedListText}
      </p>
    </div>
  )

  const productAdded = ({ image, currency, price, title, id }: SetCreatorProduct) => (
    <div
      data-testid='added-product'
      key={id}
      className='radius-xs flex h-[65px] w-full flex-row items-center gap-3 border border-darkgray border-opacity-40 bg-white p-2 lg:h-[121px] lg:gap-5 lg:rounded-lg lg:p-4'
      aria-label={data?.accessibility.addedItem}
    >
      <div
        className={
          'radius-md h-[50px] w-[50px] overflow-hidden bg-lightgray lg:h-[90px] lg:w-[90px]'
        }
      >
        <PayloadImage
          src={image}
          sizes='(min-width: 1024) 90px, 50px'
          alt={image.alt ?? ''}
          imgProps={{ className: 'radius-md' }}
          objectFit='cover'
          className={'mix-blend-darken'}
        />
      </div>
      <div>
        <p>{title}</p>
        <p className='text-darkgray'>{printMoney({ amount: price, currencyCode: currency })}</p>
      </div>
      <button
        data-testid='remove-product-button'
        className='ml-auto'
        onClick={() => data?.onDeselectVariant(id)}
        aria-label={data?.accessibility.deselectItemButton}
      >
        <Icon name='MdOutlineAdd' size={32} className='rotate-45' />
      </button>
    </div>
  )

  return (
    <>
      <div className='mx-5 grid grid-cols-10 lg:mx-10 lg:gap-x-16'>
        <div className='col-span-10 xl:col-span-6' id='left-side' ref={leftSide}>
          <ul className='grid grid-cols-6 gap-x-5 gap-y-10 md:grid-cols-12'>
            {loading
              ? new Array(8).fill(null).map((_, index) => (
                  <li key={`loading-${index}`} className='col-span-3 md:col-span-4'>
                    <ProductCard
                      loading
                      imageSizes='(min-width: 768px) 50vw, 100vw'
                      className={'aspect-[2/3] lg:aspect-[8/9]'}
                      shoppingButtonIconName='MdOutlineAdd'
                      shoppingButtonSize={windowSize.width >= 1280 ? 'normal' : 'small'}
                      disableLink={true}
                    />
                  </li>
                ))
              : data.variants.map(product => (
                  <li
                    key={product.title}
                    className='col-span-3 md:col-span-4'
                    aria-label={data.accessibility.setItem}
                  >
                    <ProductCard
                      data={{
                        ...product,
                        volume:
                          product.variants && product.variants.length > 0
                            ? product.variants[0].title
                            : undefined,
                        description: product.description || '',
                        accessibility: {
                          shoppingButtonLabel: `${data.accessibility.selectItemButton} ${product.title}`,
                          price: data.accessibility.price,
                          compareAtPrice: data.accessibility.compareAtPrice,
                        },
                      }}
                      imageSizes='(min-width: 768px) 50vw, 100vw'
                      disableDescription={!product.description}
                      className={'aspect-[2/3] lg:aspect-[8/9]'}
                      onAddToBasketClick={async () => data.onSelectVariant(product)}
                      shoppingButtonIconName='MdOutlineAdd'
                      shoppingButtonSize={windowSize.width >= 1280 ? 'normal' : 'small'}
                      disableLink={true}
                      onCardClick={() => data.onSelectVariant(product)}
                      aria-label={`${data.accessibility.selectItemButton} ${product.title}`}
                    />
                  </li>
                ))}
          </ul>
        </div>

        <div className='col-span-0 hidden xl:col-span-4 xl:block' id='right-side'>
          <LazyMotion features={motionFeatures} strict>
            <m.div className='radius-lg bg-lightgray px-12 py-10' ref={rightSide} style={{ y }}>
              <div>
                <h4>{loading ? <LoadingText textLength={15} inline /> : data.personalSetText}</h4>

                <p className='lg'>
                  {loading ? (
                    <LoadingText textLength={10} inline />
                  ) : (
                    `${data.discountText} ${data.discount}%`
                  )}
                </p>

                <div className='my-8 flex flex-col gap-5'>
                  {data?.selectedVariants.map(variant => productAdded(variant))}
                  {(!data || data.selectedVariants.length < 1) && productPlaceholder}
                  {productPlaceholder}
                </div>

                <div className='mb-8 flex flex-row items-baseline gap-3'>
                  <h4
                    data-testid='price-text'
                    aria-label={
                      loading
                        ? ''
                        : `${
                            data.discount === 0
                              ? data.accessibility.currentPrice
                              : data.accessibility.previousPrice
                          } ${data.discountedPrice}`
                    }
                  >
                    {loading ? <LoadingText textLength={5} /> : data.discountedPrice}
                  </h4>

                  {data && data.discount > 0 && (
                    <h6
                      data-testid='invalid-price-text'
                      className='text-darkgray line-through'
                      aria-label={`${data.accessibility.previousPrice} ${data.totalPrice}`}
                    >
                      {data.totalPrice}
                    </h6>
                  )}
                </div>

                <Button
                  data-testid='add-to-basket-button'
                  appearance='dark'
                  onPress={data?.onBuyButtonClick}
                  isLoading={data?.isSubmitting || loading}
                >
                  {data?.buyButtonText}
                </Button>
              </div>
            </m.div>
          </LazyMotion>
        </div>
      </div>
      <div className='fixed bottom-0 z-10 flex max-h-[90vh] w-full flex-col justify-between gap-x-12 gap-y-4 bg-white p-5 shadow-[0px_10px_1px_rgba(221,_221,_221,_1),_0_10px_20px_rgba(204,_204,_204,_1)] md:px-10 md:py-6 lg:flex-row-reverse xl:hidden'>
        <div className='flex flex-1 flex-row justify-between gap-x-8'>
          <div className='flex h-[57px] flex-col justify-center whitespace-nowrap lg:h-[75px] '>
            <p
              aria-label={
                loading
                  ? ''
                  : data.discount === 0
                  ? data.accessibility.currentPrice
                  : data.accessibility.previousPrice
              }
              className='h6'
            >
              {loading ? <LoadingText textLength={5} /> : data.discountedPrice}
            </p>

            {data && data.discount > 0 && (
              <p
                className='h6 text-darkgray line-through'
                aria-label={data.accessibility.previousPrice}
              >
                {data.totalPrice}
              </p>
            )}
          </div>
          <Button
            appearance='dark'
            onPress={data?.onBuyButtonClick}
            isLoading={data?.isSubmitting || loading}
            className='h-[57px] !max-w-[440px] lg:h-[75px] lg:rounded-sm'
          >
            {data?.buyButtonText}
          </Button>
        </div>
        <div className='radius-md flex min-h-[75px] w-full flex-1 flex-col bg-lightgray px-6 py-4'>
          <button
            className='flex h-[50px] items-center justify-between'
            onClick={() => setProductDropdownOpen(prev => !prev)}
          >
            <div className='text-left'>
              <p>
                {loading ? (
                  <LoadingText textLength={10} />
                ) : (
                  `${data.selectedVariants.length} ${data.addProductText}`
                )}
              </p>
              {data && data.discount > 0 && (
                <p className='sm'>
                  {data.discountText} {data.discount}%
                </p>
              )}
            </div>
            <Icon name='MdKeyboardArrowDown' size={32} className='mr-1' />
          </button>
          <div
            className={clsx('flex flex-col gap-y-2 transition-all duration-500 ease-in-out', {
              'overflow-hidden': !productDropdownOpen,
              'overflow-scroll': productDropdownOpen,
              'max-h-0 opacity-0': !productDropdownOpen,
              'opacity-1 mt-5 max-h-[2000px]': productDropdownOpen,
            })}
          >
            {data?.selectedVariants.map(variant => productAdded(variant))}
            {(!data || data.selectedVariants.length < 1) && productPlaceholder}
          </div>
        </div>
      </div>
    </>
  )
}
